import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Observable, Subject, firstValueFrom } from "rxjs";
import { IDashboardModel } from "src/app/views-interfaces/dashboard-model.interface";
import {
    ICategories,
    ICategoriesApiResponse,
    ICategoryCreateResponse,
    ICategoryData,
    ICategoryDeleteResponse,
    ICategoryUpdateResponse,
    ISaveCategoriesData,
} from "../../views-interfaces/categories.interface";
import { IT1Tags } from "../../views-interfaces/t1-tags.interface";
import { AppSettingsService } from "../app-settings-service/app-settings.service";
import { AuthService } from "../auth";
import { DashboardsService } from "../dashboards-service/dashboards.service";
import { ResponseErrorHandlerService } from "../response-error-handler-service/response-error-handler.service";
import { UtilsService } from "../utils/utils.service";

@Injectable()
export class CategoriesService {
    private http = inject(HttpClient);
    private readonly authService = inject(AuthService);
    private appSettingsService = inject(AppSettingsService);
    private readonly dashboardsService = inject(DashboardsService);
    private responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private dashboardId: string;
    private lastDashboardId: string;

    categoriesLoadedEmitter = new Subject();
    categoriesLoadedEvent$ = this.categoriesLoadedEmitter.asObservable();

    private categoriesLoaded = false;
    private categoriesCache: Map<string, Promise<ICategories[]>> = new Map<string, Promise<ICategories[]>>();
    private Categories: ICategories[] = [];

    isSaving = 0;
    inError = false;

    constructor() {
        this.authService.fetchedUserInfoEvent$.subscribe(async () => {
            this.dashboardId = await this.dashboardsService.getSafeCurrentDashboardId();
        });

        this.dashboardsService.dashboardChangeEvent$.subscribe(async () => {
            this.categoriesLoaded = false;
        });

        this.dashboardsService.currentDashboardChanged$.subscribe(async () => {
            this.dashboardId = this.dashboardsService.getCurrentDashboardId();
            if (this.categoriesLoaded) {
                this.refresh(true);
            }
        });
    }

    refresh(forceReload = false) {
        this.categoriesLoaded = false;
        this.calculateCategoryCache = {};
        this.Categories = [];
        this.categoriesCache = new Map<string, Promise<ICategories[]>>();
        if (forceReload) {
            this.categoriesCache.set(this.dashboardId, this.loadCategories());
        }
    }

    async loadCategories() {
        try {
            const result = await firstValueFrom(
                this.http.get<ICategoriesApiResponse>(this.getUrl(), {
                    headers: new HttpHeaders().set("Content-Type", "application/json"),
                }),
            );
            if (result.Code == 200) {
                this.Categories = result.Categories;
            }
            return result.Categories;
        } catch (error) {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        }
    }

    setCategories(categories: ICategories[]) {
        this.Categories = categories;
        this.calculateCategoryCache = {};
    }

    async getCategoriesList() {
        this.dashboardId = await this.dashboardsService.getSafeCurrentDashboardId();
        if (!this.categoriesCache.get(this.dashboardId)) {
            this.categoriesCache.set(this.dashboardId, this.loadCategories());
        }
        if (this.lastDashboardId !== this.dashboardId) {
            this.categoriesLoaded = true;
            this.categoriesLoadedEmitter.next(true);
            this.lastDashboardId = this.dashboardId;
        }
        return this.categoriesCache.get(this.dashboardId) ?? [];
    }

    getCategory(id: string) {
        return this.Categories.find((c) => c.Id === id);
    }

    addCategory(categoryData: ICategoryData) {
        return this.http.post<ICategoryCreateResponse>(this.getUrl(), categoryData, {
            headers: new HttpHeaders().set("Content-Type", "application/json"),
        });
    }

    updateCategory(categoryData: ICategoryData | ISaveCategoriesData["updateCategory"]) {
        this.Categories = undefined;
        return this.http.put<ICategoryUpdateResponse>(this.getUrl(), categoryData, {
            headers: new HttpHeaders().set("Content-Type", "application/json"),
        });
    }

    deleteCategory(categoryId: string) {
        this.Categories = undefined;
        return this.http.delete<ICategoryDeleteResponse>(this.getUrl() + categoryId);
    }

    private getUrl(path = "categories/") {
        return (
            this.appSettingsService.appSettings.CategoriesService_URL +
            this.appSettingsService.appSettings.CategoriesService_Version +
            path +
            this.dashboardId +
            "/"
        );
    }

    fillTagsByIdForCategories(categories: ICategories[], tags: IT1Tags[]): ICategories[] {
        const categoriesWhitTags = categories.map((category: ICategories) => {
            if (category.Tags.length > 0) {
                category.Tags.forEach((tag, i) => {
                    tags.forEach((element) => {
                        if (element.Id === category.Tags[i]) {
                            category.Tags[i] = UtilsService.recursiveDeepCopy(element);
                        }
                    });
                });
            }
            return category;
        });
        return categoriesWhitTags;
    }

    sortByOrder(a: ICategories, b: ICategories) {
        if (a?.Order == null && b?.Order == null) {
            return 0;
        } else if ((a?.Order || a?.Order === 0) && b?.Order == null) {
            return 1;
        } else if (a?.Order == null && (b?.Order || b?.Order === 0)) {
            return -1;
        } else {
            return a?.Order - b?.Order;
        }
    }

    saveCategories(fileCategoryModelCategories) {
        return new Observable((observer) => {
            this.isSaving = 0;
            this.inError = false;
            const preparedData = {
                addCategory: [],
                updateCategory: {},
            };
            this.Categories.forEach((category, Order) => {
                if (category.tempId) {
                    const tempCategory = {
                        Tags: [],
                        Name: {},
                        Order: Order,
                    };
                    category.Tags.forEach((element, i) => {
                        tempCategory.Tags.push(category.Tags[i].Id);
                    });
                    tempCategory.Name = category.Name;
                    preparedData.addCategory.push(tempCategory);
                } else {
                    preparedData.updateCategory[category.Id] = {
                        Name: {},
                        Tags: [],
                        Order: Order,
                    };
                    preparedData.updateCategory[category.Id].Name = category.Name;
                    category.Tags.forEach((element, i) => {
                        preparedData.updateCategory[category.Id].Tags.push(category.Tags[i].Id);
                    });
                }
            });
            if (preparedData.addCategory.length > 0) {
                preparedData.addCategory.forEach((newCategory: ICategoryData) => {
                    this.isSaving = ++this.isSaving;
                    this.addCategory(newCategory).subscribe(
                        (response: ICategoryCreateResponse) => {
                            fileCategoryModelCategories[response.Category.Order].Id = response.Category.Id;
                            delete fileCategoryModelCategories[response.Category.Order].tempId;
                            this.isSaving = --this.isSaving;
                            this.returnObservableIfNeeded(observer);
                        },
                        () => {
                            this.isSaving = --this.isSaving;
                            this.inError = true;
                            this.returnObservableIfNeeded(observer);
                        },
                    );
                });
            }

            if (Object.getOwnPropertyNames(preparedData.updateCategory).length > 0) {
                this.isSaving = ++this.isSaving;
                this.updateCategory(preparedData.updateCategory).subscribe(
                    (response: ICategoryUpdateResponse) => {
                        this.isSaving = --this.isSaving;
                        this.returnObservableIfNeeded(observer);
                    },
                    () => {
                        this.isSaving = --this.isSaving;
                        this.inError = true;
                        this.returnObservableIfNeeded(observer);
                    },
                );
            }
        });
    }

    returnObservableIfNeeded(observer) {
        if (this.isSaving === 0) {
            if (this.inError) {
                observer.error();
                observer.complete();
            } else {
                observer.next();
                observer.complete();
            }
        }
    }

    private calculateCategoryCache = {};
    calculateCategoryWithCategories(
        currentCategories: ICategories[],
        currentDashboardAssignCategoryNoTags: boolean | string,
        tags: string[],
    ): ICategories {
        if (Object.hasOwn(this.calculateCategoryCache, tags.length + tags.join(""))) {
            return this.calculateCategoryCache[tags.length + tags.join("")];
        }
        let matchingCategory = null;
        let matchingCategoryOrder = null;
        if (tags.length > 0) {
            // Find the matching category with lowest Order value
            tags.forEach((tag) => {
                const category = currentCategories.find((cat) => cat.Tags.includes(tag));

                if (category) {
                    if (matchingCategoryOrder === null || category.Order < matchingCategoryOrder) {
                        matchingCategory = category;
                        matchingCategoryOrder = category.Order;
                    }
                }
            });
        }
        if (!matchingCategory) {
            // If no tags are supplied or tags not in a category, we have a setting to allow returning the rightmost category
            if (typeof currentDashboardAssignCategoryNoTags === "boolean") {
                if (currentDashboardAssignCategoryNoTags) {
                    // Find the matching category with highest Order value
                    currentCategories.forEach((category) => {
                        if (matchingCategoryOrder === null || category.Order > matchingCategoryOrder) {
                            matchingCategory = category;
                            matchingCategoryOrder = category.Order;
                        }
                    });
                }
            } else {
                if (currentDashboardAssignCategoryNoTags) {
                    // Find the matching category
                    matchingCategory = currentCategories.find((category) => category.Id == currentDashboardAssignCategoryNoTags);
                }
            }
        }

        this.calculateCategoryCache[tags.length + tags.join("")] = matchingCategory;
        return matchingCategory;
    }

    calculateCategory(currentDashboard: IDashboardModel, tags: string[]): ICategories {
        const assignCategoryNoTags = currentDashboard.TaxYear < 2025 ? currentDashboard.AssignCategoryNoTags : currentDashboard.AssignCategoryId;
        return this.calculateCategoryWithCategories(this.Categories, assignCategoryNoTags, tags);
    }
}
